import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {FaIcon} from '../../FaIcon';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {requestDriveAuthCode, pickDriveFiles} from '../../../utils/googleDrivePicker';

// Señales de cancelación intencional del popup de OAuth: NO se muestran como error (igual que el
// cancel del Picker, que es silencioso).
const CANCEL_SIGNALS = ['popup_closed', 'access_denied', 'no_code', 'cancel', 'closed', 'dismissed'];

function isUserCancellation(e) {
    const msg = (e && e.message ? e.message : '').toLowerCase();
    return CANCEL_SIGNALS.some((s) => msg.includes(s));
}

function maxMb(config) {
    const bytes = config && config.admission ? config.admission.max_bytes : 0;
    return Math.round((bytes || 0) / (1024 * 1024));
}

// Wizard de conexión de ARCHIVOS de Drive (archivo-único):
//   Paso 1 (intro):  guía "qué podés sumar" (tipos + límite) + botón para elegir archivos.
//   Paso 2 (review): tras el Picker, tabla de admisión (aceptado / rechazado con motivo) -> confirmar.
// Parametrizable por propósito (Media Plan Sync reusa el mismo flujo con otra copy y otros mimes):
//   `purpose`   -> viaja en validate/create (backend scopea admisión/dedup por propósito).
//   `i18nBase`  -> namespace de textos (default: los de la Memoria). Los motivos de rechazo caen
//                  al namespace de la Memoria si el propósito no define uno propio.
//   `mimeTypes` -> filtro del Picker (default: los tipos soportados por la Memoria).
export function DriveConnectModal({isOpen, onClose, clientId, config, onConnected,
                                   purpose, i18nBase, mimeTypes}) {
    const {t} = useTranslation();
    const [busy, setBusy] = useState(false);
    const [credentialRef, setCredentialRef] = useState(null);
    const [picked, setPicked] = useState(null);         // docs elegidos en el Picker
    const [validation, setValidation] = useState(null); // [{id, name, accepted, reason_code}]

    const base = i18nBase || 'client_memory.connections';
    // Propósito por defecto: MEMORY (el media plan pasa purpose='MEDIA_PLAN' explícito). La guía
    // de "documentos recomendados" solo aplica a la Memoria — el media plan conecta una planilla.
    const isMemoryPurpose = !purpose || purpose === 'MEMORY';
    const tt = (suffix, opts) => t(`${base}.${suffix}`, opts);
    const reasonLabel = (code) => t(`${base}.modal.reason.${code}`, {
        defaultValue: t(`client_memory.connections.modal.reason.${code}`,
            {defaultValue: tt('modal.rejected_generic')}),
    });
    const purposePayload = purpose ? {purpose} : {};

    const step = validation ? 'review' : 'intro';
    const acceptedCount = validation ? validation.filter((r) => r.accepted).length : 0;

    const reset = () => {
        setCredentialRef(null);
        setPicked(null);
        setValidation(null);
    };

    // Arrepentirse de UN archivo sin rehacer todo el Picker. Sale de las dos listas: `validation`
    // pinta la tabla y `picked` es lo que se manda en connect() — filtrar solo una dejaría el
    // archivo fuera de la vista pero adentro del POST.
    const removeFile = (id) => {
        if (busy) return;
        setPicked((prev) => (prev || []).filter((d) => d.id !== id));
        setValidation((prev) => (prev || []).filter((r) => r.id !== id));
    };

    const close = () => {
        if (busy) return;
        reset();
        onClose();
    };

    // Paso 2: OAuth + Picker (multi-archivo) + validate.
    const pickAndValidate = async () => {
        setBusy(true);
        try {
            const code = await requestDriveAuthCode(config.client_id);
            const exchange = await ClientMemoryApi.exchangeDriveCode(code);
            if (!exchange || !exchange.access_token || !exchange.credential_ref) {
                toast.error(tt('toast.error'));
                return;
            }
            const docs = await pickDriveFiles({
                accessToken: exchange.access_token,
                developerKey: config.picker_api_key,
                appId: config.app_id,
                mimeTypes: mimeTypes || (config.admission ? config.admission.supported_mimes : []),
            });
            if (!docs) return;  // el usuario canceló el Picker
            setCredentialRef(exchange.credential_ref);
            setPicked(docs);
            const result = await ClientMemoryApi.validateFiles({
                agency_client_id: clientId,
                files: docs.map((d) => ({id: d.id, name: d.name})),
                credential_ref: exchange.credential_ref,
                ...purposePayload,
            });
            if (result && result.status === 'TOKEN_REVOKED') {
                toast.error(tt('modal.reconnect_needed'));
                reset();
                return;
            }
            if (!result || !Array.isArray(result.results)) {
                toast.error(tt('toast.error'));
                return;
            }
            setValidation(result.results);
        } catch (e) {
            if (!isUserCancellation(e)) toast.error(tt('toast.error'));
        } finally {
            setBusy(false);
        }
    };

    // Paso 3: crea/reconecta los archivos aceptados. El backend re-valida (no confía en el FE).
    const connect = async () => {
        setBusy(true);
        try {
            const created = await ClientMemoryApi.createConnection({
                agency_client_id: clientId,
                files: picked.map((d) => ({id: d.id, name: d.name, web_view_link: d.url})),
                credential_ref: credentialRef,
                ...purposePayload,
            });
            if (created && created.status === 'OK') {
                const n = (created.created || []).length + (created.rebound || []).length;
                if (n > 0) {
                    // Al terminar de conectar, lo mismo que prometió la intro: se actualizan solos.
                    toast.success(isMemoryPurpose
                        ? t('client_memory.sources.connected_toast', {count: n})
                        : tt('modal.connected_n', {count: n}));
                    reset();
                    onConnected();
                } else {
                    toast.error(tt('modal.none_accepted'));
                }
            } else if (created && created.status === 'TOKEN_REVOKED') {
                toast.error(tt('modal.reconnect_needed'));
            } else {
                toast.error(tt('toast.error'));
            }
        } catch (e) {
            toast.error(tt('toast.error'));
        } finally {
            setBusy(false);
        }
    };

    const renderIntro = () => (
        <div className="dcm-intro">
            <p className="dcm-intro-lead">{tt('modal.file_intro')}</p>
            <div className="dcm-guide">
                <p className="dcm-guide-title">{tt('modal.what_you_can_add')}</p>
                <ul className="dcm-guide-list">
                    {isMemoryPurpose && (
                        <li>
                            <FaIcon iconName="handshake" className="icon"/>
                            {t('client_memory.connections.modal.recommended_docs')}
                        </li>
                    )}
                    <li>
                        <FaIcon iconName="file-lines" className="icon"/>
                        {tt('modal.supported_types')}
                    </li>
                    <li>
                        <FaIcon iconName="weight-hanging" className="icon"/>
                        {tt('modal.size_limit', {mb: maxMb(config)})}
                    </li>
                </ul>
            </div>
            {/* Promesa de la sincronización diaria (frente «chicos» de la tanda Hesta, 01/10). Es
                la respuesta a «¿quedan sincronizados o se sube una copia?» en el momento en que
                aparece la duda. Bloque propio para que el rediseño del modal («Drive por usuario»)
                lo conserve tal cual. Sólo para la Memoria: el media plan tiene su propio «Releer». */}
            {isMemoryPurpose && (
                <p className="dcm-auto-sync">
                    <FaIcon iconName="rotate" className="icon"/>
                    {t('client_memory.sources.auto_sync_promise')}
                </p>
            )}
            <div className="dcm-intro-actions">
                <button type="button" className="btn -alt" disabled={busy} onClick={pickAndValidate}>
                    <FaIcon prefix="fab" iconName="google-drive" className="icon"/>
                    {busy ? tt('modal.checking') : tt('modal.choose_files')}
                </button>
            </div>
        </div>
    );

    const renderReview = () => (
        <div className="dcm-review">
            {validation.length > 0 ? (
                <p className="dcm-review-lead">{tt('modal.review_lead')}</p>
            ) : (
                <div className="dcm-file-empty">
                    <FaIcon iconName="folder-open" className="dcm-file-empty-icon"/>
                    <p>{tt('modal.none_left')}</p>
                </div>
            )}
            <ul className="dcm-file-list">
                {validation.map((r) => {
                    const isDup = r.reason_code === 'DUPLICATE';
                    const cls = r.accepted ? (isDup ? '-dup' : '-ok') : '-rejected';
                    return (
                        <li key={r.id} className={`dcm-file-row ${cls}`}>
                            <FaIcon iconName={r.accepted ? (isDup ? 'rotate' : 'circle-check') : 'circle-xmark'}
                                    className="dcm-file-icon"/>
                            <span className="dcm-file-name">{r.name}</span>
                            <span className="dcm-file-tag">
                                {r.accepted && !isDup && tt('modal.will_add')}
                                {r.accepted && isDup && reasonLabel('DUPLICATE')}
                                {!r.accepted && reasonLabel(r.reason_code)}
                            </span>
                            <button type="button" className="dcm-file-remove" disabled={busy}
                                    aria-label={tt('modal.remove_file',
                                        {name: r.name, interpolation: {escapeValue: false}})}
                                    onClick={() => removeFile(r.id)}>
                                <FaIcon iconName="xmark"/>
                            </button>
                        </li>
                    );
                })}
            </ul>
            <button type="button" className="btn -alt -negative dcm-review-back" disabled={busy}
                    onClick={reset}>
                {tt('modal.choose_other')}
            </button>
        </div>
    );

    return (
        <Modal isOpen={isOpen} toggle={close} className="dcm-modal">
            <ModalHeader toggle={close}>
                <span className="dcm-title">
                    <FaIcon prefix="fab" iconName="google-drive"/>
                    {tt('modal.title')}
                </span>
            </ModalHeader>
            <ModalBody>
                {step === 'intro' ? renderIntro() : renderReview()}
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -negative" disabled={busy} onClick={close}>
                    {t('common.cancel')}
                </button>
                {step === 'review' && (
                    <button type="button" className="btn -alt" disabled={busy || acceptedCount === 0}
                            onClick={connect}>
                        {busy ? tt('connecting') : tt('modal.connect_n', {count: acceptedCount})}
                    </button>
                )}
            </ModalFooter>
        </Modal>
    );
}
